import {Button, Card, Col, Row, Table, Tag} from "antd"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import React, {useCallback, useEffect, useMemo} from "react"
import {Loading} from "../../components/Loading/Loading"
import {isActiveOptionsSelect, limitOptionsSelect} from "../../helps/optionsSelect"
import {useHistory, useLocation} from "react-router-dom"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import {center} from "../../helps/tableHelper"
import {Helmet} from "react-helmet-async"
import {FormSelect} from "../../components/Form/FormSelect"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {getUsers, setUsers, setUsersFilter, setWSUser} from "../../store/userReducer"
import {useGetCompanies, useGetUserGroups} from "../../hooks/useFetchHooks"
import {getUserRoles} from "../../store/appReducer"
import {FormInput} from "../../components/Form/FormInput"

export const Users = () =>
{
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const users = useSelector((state: StateType) => state.userReducer.users)
    const filter = useSelector((state: StateType) => state.userReducer.filter)
    const userRoles = useSelector((state: StateType) => state.appReducer.userRoles)
    const ws = useSelector((state: StateType) => state.userReducer.ws)
    const controller = useMemo(() => new AbortController(), [])

    const companies = useGetCompanies()

    const userGroupsQuery = useMemo(() => ({
        idCompany: filter.idCompany,
    }), [filter.idCompany])
    const userGroups = useGetUserGroups(userGroupsQuery)

    useEffect(() =>
    {
        dispatch(getUserRoles({controller}))
    }, [dispatch, controller])

    const columns = useMemo(() =>
    {
        const cols = []

        if (profile?.idCompany === ID_SYSTEM_COMPANY) cols.push({
            title: "Компания",
            dataIndex: "nameCompany",
            key: "nameCompany",
            align: center,
        })

        cols.push(
            {
                title: "Группа",
                dataIndex: "nameUserGroup",
                key: "nameUserGroup",
                align: center,
            },
            {
                title: "Роль",
                dataIndex: "nameUserType",
                key: "nameUserType",
                align: center,
            },
            {
                title: "ФИО",
                dataIndex: "name",
                key: "name",
                align: center,
            },
            {
                title: "Email",
                dataIndex: "email",
                key: "email",
                align: center,
            },
            {
                title: "Статус",
                dataIndex: "active",
                key: "active",
                render: (active: boolean) =>
                {
                    return (
                        <Tag color={active ? "green" : "volcano"} style={{marginRight: 0}}>{active ? "Активно" : "Не активно"}</Tag>
                    )
                },
                align: center,
            },
        )

        return cols
    }, [profile])

    useEffect(() =>
    {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter))
        {
            dispatch(setUsersFilter({...filterString}))
        }
        else if (location.search !== filterToString(filter))
        {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getUsersFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (location?.search)
        {
            dispatch(getUsers({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() =>
    {
        getUsersFunc()

        return () =>
        {
            dispatch(setUsers(null))
        }
    }, [dispatch, getUsersFunc])

    useEffect(() =>
    {
        return () =>
        {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() =>
    {
        if (ws)
        {
            getUsersFunc(true)
        }
        dispatch(setWSUser(null))
    }, [dispatch, ws, getUsersFunc])

    const data = useMemo(() => users?.data?.map(i =>
    {
        return {
            key: i.id,
            id: i.id,
            nameCompany: i.nameCompany,
            name: i.name,
            email: i.email,
            nameUserType: i.nameUserType,
            nameUserGroup: i.nameUserGroup,
            active: i.active,
        }
    }), [users])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({label: i.name, value: i.id})), [companies])
    const userGroupOptions = useMemo(() => userGroups?.data?.map(i => ({label: i.name, value: i.id})), [userGroups])
    const userRoleOptions = useMemo(() => userRoles?.data?.map(i => ({label: i.name, value: i.id})), [userRoles])

    return (
        <div className="tabled">
            <Helmet>
                <title>Пользователи</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title={
                              <div className="table-title">
                                  <div>Пользователи</div>
                                  <Button onClick={() => history.push("/userForm")} type="link" size="middle" style={{marginTop: 5}}>
                                      Добавить
                                  </Button>
                              </div>
                          }
                          extra={
                              <div className="table-title-extra">
                                  <FormInput placeholder="Поиск"
                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                             value={filter?.search}
                                             name="search"
                                             onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                             allowClear
                                  />

                                  <FormSelect value={filter?.idUserType}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, idUserType: value, offset: 0})})}
                                              options={userRoleOptions}
                                              placeholder="Роль"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  {
                                      profile?.idCompany === ID_SYSTEM_COMPANY
                                          ?
                                          <FormSelect value={filter?.idCompany}
                                                      containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                      dropdownStyle={{minWidth: "min-content"}}
                                                      onChange={(value) => history.replace({search: filterToString({...filter, idCompany: value, idUserGroup: "", offset: 0})})}
                                                      options={companyOptions}
                                                      placeholder="Компания"
                                                      allowClear
                                                      placement="bottomRight"
                                          />
                                          : null
                                  }

                                  <FormSelect value={filter?.idUserGroup}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, idUserGroup: value, offset: 0})})}
                                              options={userGroupOptions}
                                              placeholder="Группа"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.status}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, status: value, offset: 0})})}
                                              options={isActiveOptionsSelect}
                                              placeholder="Статус"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive">
                            {
                                users
                                    ? <Table columns={columns}
                                             dataSource={data}
                                             pagination={filter?.limit ? {
                                                 position: users?.data?.length >= 10 ? ["bottomLeft", "topLeft"] : ["bottomLeft"],
                                                 pageSize: filter.limit,
                                                 hideOnSinglePage: true,
                                                 total: users.total,
                                                 current: filter?.offset / filter?.limit + 1,
                                                 onChange: (page, pageSize) =>
                                                 {
                                                     const obj = {
                                                         limit: pageSize,
                                                         offset: (page - 1) * pageSize,
                                                     }
                                                     history.replace({search: filterToString({...filter, ...obj})})
                                                 },
                                                 showSizeChanger: false,
                                             } : false}
                                             className="ant-border-space"
                                             onRow={({id}) => ({
                                                 onClick: () => history.push(`/user/${id}`),
                                                 onMouseDown: (e) => e.button === 1 && window.open(`/user/${id}`, "_blank", "noopener,noreferrer"),
                                             })}
                                             rowClassName={({active}) => active ? "" : "table-row-deleted"}
                                    />
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}
